import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get, remove } from "firebase/database";
import { app } from "./firebase/config";
import "./AdminPriceAnalysisManagement.css";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FiRefreshCw,
  FiActivity,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiFileText,
  FiDownload,
  FiEye,
  FiClock,
  FiMapPin,
  FiHome,
  FiArchive,
  FiUsers,
  FiLoader,
  FiTrash2,
  FiBookOpen,
  FiSearch,
  FiPercent,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

// Import new utility and components
import {
  getPropertyTypeIcon,
  parsePropertyInfoFromMessage,
  formatStructuredAnalysis,
  getMarketTrendInfo,
  getConfidenceLevel,
  analyzeError,
  getErrorBadge,
  getErrorIcon,
  getErrorSuggestedAction,
} from "./utils/analysisFormatter";
import GeneralInfoSection from "./components/AdminAnalysis/GeneralInfoSection";
import PropertyDetailsSection from "./components/AdminAnalysis/PropertyDetailsSection";
import AnalysisResultSection from "./components/AdminAnalysis/AnalysisResultSection";
import ErrorSection from "./components/AdminAnalysis/ErrorSection";
import AnalysisDetailModal from "./components/AdminAnalysis/AnalysisDetailModal";

// Chuẩn hoá dữ liệu analysis_result thành chuỗi dễ đọc cho UI
const formatAnalysisResult = (analysisResult) => {
  if (!analysisResult || typeof analysisResult !== "object") return "";

  const infoLines = [];
  const sections = [];

  const addInfoLine = (label, value) => {
    if (value) {
      infoLines.push(`${label}: ${value}`);
    }
  };

  addInfoLine("Giá ước tính", analysisResult.estimatedPrice);
  addInfoLine("Giá theo m²", analysisResult.pricePerM2);
  addInfoLine("Xu hướng thị trường", analysisResult.marketTrend);
  addInfoLine("Độ tin cậy", analysisResult.confidence);

  if (infoLines.length > 0) {
    sections.push(infoLines.join("\n"));
  }

  if (analysisResult.summary) {
    sections.push(`Tóm tắt:\n${analysisResult.summary}`);
  }

  const addListSection = (title, list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    const lines = list.map((item) => `- ${item}`);
    sections.push(`${title}:\n${lines.join("\n")}`);
  };

  addListSection("Nhận định thị trường", analysisResult.marketInsights);
  addListSection("Khuyến nghị", analysisResult.recommendations);

  const formatted = sections.join("\n\n").trim();
  if (formatted) return formatted;

  try {
    return JSON.stringify(analysisResult, null, 2);
  } catch (error) {
    return "Phân tích hoàn thành";
  }
};

const AdminPriceAnalysisManagement = () => {
  const [queueMessages, setQueueMessages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [analysisPerPage] = useState(10);
  const [stats, setStats] = useState({
    totalAnalysis: 0,
    completedAnalysis: 0,
    pendingAnalysis: 0,
    errorAnalysis: 0,
    successRate: 0,
  });
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // State cho markdown viewer
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const [markdownContent, setMarkdownContent] = useState("");
  const [markdownTitle, setMarkdownTitle] = useState("");
  const [loadingMarkdown, setLoadingMarkdown] = useState(false);

  // State cho full report modal
  const [showFullReportModal, setShowFullReportModal] = useState(false);

  // Helper function: Xác định loại định giá (bán hay thuê)
  const getPriceType = useCallback((item) => {
    const title =
      item.result?.updated_page_data?.current_task_title || item.message || "";
    if (title.toLowerCase().includes("thuê")) {
      return {
        label: "Thuê BDS",
        bgColor: "bg-purple-100",
        textColor: "text-purple-700",
        borderColor: "border-purple-300",
        icon: "🏠",
      };
    }
    return {
      label: "Bán BDS",
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      borderColor: "border-blue-300",
      icon: "💰",
    };
  }, []);

  // Toast notification function
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  const fetchQueueMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const db = getDatabase(app);

      // Thay đổi: Lấy dữ liệu từ histories thay vì queue_message để có file results
      const historiesRef = ref(db, "histories");
      const snapshot = await get(historiesRef);

      if (snapshot.exists()) {
        const data = snapshot.val();

        // Chuyển đổi dữ liệu từ structure histories/${userId}/${taskId} thành flat array
        const flattenedData = {};
        Object.entries(data).forEach(([userId, userHistories]) => {
          if (userHistories && typeof userHistories === "object") {
            Object.entries(userHistories).forEach(([taskId, taskData]) => {
              const assistantMessageRaw = taskData.analysis_result;
              const assistantMessage =
                typeof assistantMessageRaw === "string"
                  ? assistantMessageRaw
                  : formatAnalysisResult(assistantMessageRaw);

              const safeAssistantMessage =
                (assistantMessage && assistantMessage.trim()) ||
                taskData.manus_response?.markdown ||
                "Phân tích hoàn thành";

              flattenedData[taskId] = {
                ...taskData,
                taskId,
                userId,
                // Map để tương thích với structure cũ
                request_id: taskData.request_id || taskId,
                result: {
                  success:
                    taskData.status === "completed" ||
                    taskData.status === "success" ||
                    taskData.status === "uploaded", // Thêm "uploaded" vì đây là status khi task hoàn thành thành công
                  crawl_response: taskData.crawl_response,
                  updated_page_data: {
                    current_task_title:
                      taskData.current_task_title || taskData.message,
                    page_title: taskData.current_task_title || taskData.message,
                  },
                  chat_response: {
                    user_message: taskData.message,
                    assistant_message: safeAssistantMessage,
                  },
                  task_id: taskId,
                },
                completed_at: taskData.updated_at || taskData.created_at,
              };
            });
          }
        });

        setQueueMessages(flattenedData);
        calculateStats(flattenedData);
        showToast("Dữ liệu đã được cập nhật từ histories!");
      } else {
        setQueueMessages({});
        setStats({
          totalAnalysis: 0,
          completedAnalysis: 0,
          pendingAnalysis: 0,
          errorAnalysis: 0,
          successRate: 0,
        });
      }
    } catch (err) {
      console.error("Error fetching histories:", err);
      setError("Có lỗi khi tải dữ liệu phân tích giá từ histories");
      showToast("Lỗi khi tải dữ liệu: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch queue messages data
  useEffect(() => {
    fetchQueueMessages();
  }, [fetchQueueMessages]);

  // Calculate statistics
  const calculateStats = (data) => {
    let totalAnalysis = 0;
    let completedAnalysis = 0;
    let pendingAnalysis = 0;
    let errorAnalysis = 0;

    Object.values(data).forEach((analysis) => {
      totalAnalysis++;

      // Cập nhật logic để tương thích với dữ liệu từ histories
      const status = analysis.status?.toLowerCase();
      const resultSuccess = analysis.result?.success;

      if (
        status === "completed" ||
        status === "success" ||
        status === "uploaded" || // Thêm "uploaded" vào completed analysis
        resultSuccess === true
      ) {
        completedAnalysis++;
      } else if (
        status === "failed" ||
        status === "error" ||
        status === "crawl_failed" || // Thêm "crawl_failed" vào error analysis
        resultSuccess === false
      ) {
        errorAnalysis++;
      } else {
        pendingAnalysis++;
      }
    });

    const successRate =
      totalAnalysis > 0
        ? ((completedAnalysis / totalAnalysis) * 100).toFixed(2)
        : 0;

    setStats({
      totalAnalysis,
      completedAnalysis,
      pendingAnalysis,
      errorAnalysis,
      successRate: parseFloat(successRate),
    });
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("vi-VN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Get all analysis requests as array
  const getAllAnalysis = () => {
    const analysis = [];
    Object.entries(queueMessages).forEach(([id, data]) => {
      analysis.push({
        id,
        ...data,
      });
    });
    return analysis.sort(
      (a, b) =>
        new Date(b.completed_at || b.created_at || 0) -
        new Date(a.completed_at || a.created_at || 0)
    );
  };

  // Filter analysis
  const getFilteredAnalysis = () => {
    let analysis = getAllAnalysis();

    // Filter by search term
    if (searchTerm) {
      analysis = analysis.filter(
        (item) =>
          item.request_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.result?.chat_response?.user_message
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.result?.updated_page_data?.current_task_title
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      if (statusFilter === "completed") {
        analysis = analysis.filter((item) => item.result?.success === true);
      } else if (statusFilter === "error") {
        analysis = analysis.filter((item) => item.result?.success === false);
      } else if (statusFilter === "pending") {
        analysis = analysis.filter(
          (item) => !item.result || item.result.success === undefined
        );
      }
    }

    return analysis;
  };

  // Delete analysis record
  const deleteAnalysisRecord = async (id) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa bản ghi phân tích này?"))
      return;

    try {
      const db = getDatabase(app);

      // Tìm record để lấy userId và taskId
      const analysisToDelete = Object.values(queueMessages).find(
        (analysis) => analysis.taskId === id || analysis.id === id
      );

      if (!analysisToDelete) {
        showToast("Không tìm thấy bản ghi để xóa!", "error");
        return;
      }

      // Xóa từ histories/${userId}/${taskId}
      const recordRef = ref(
        db,
        `histories/${analysisToDelete.userId}/${analysisToDelete.taskId}`
      );
      await remove(recordRef);

      // Refresh data
      await fetchQueueMessages();
      showToast("Đã xóa bản ghi thành công!");
    } catch (err) {
      console.error("Error deleting analysis record:", err);
      showToast("Có lỗi khi xóa bản ghi: " + err.message, "error");
    }
  };

  // Status presentation helper để tái sử dụng UI giống PriceHistoryScreen
  const getStatusInfo = useCallback((analysis) => {
    const status = analysis?.status?.toLowerCase();
    const resultSuccess = analysis?.result?.success;

    if (
      resultSuccess === true ||
      status === "completed" ||
      status === "success" ||
      status === "uploaded"
    ) {
      return {
        icon: <FiCheckCircle />,
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        text: "Hoàn thành",
      };
    }

    if (
      resultSuccess === false ||
      status === "failed" ||
      status === "error" ||
      status === "crawl_failed"
    ) {
      return {
        icon: <FiXCircle />,
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        text: "Lỗi",
      };
    }

    if (status === "pending") {
      return {
        icon: <FiClock />,
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
        text: "Chờ xử lý",
      };
    }

    return {
      icon: <FiLoader className="animate-spin" />,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      text: "Đang xử lý",
    };
  }, []);

  // Extract property info from user message
  const extractPropertyInfo = (userMessage) => {
    if (!userMessage) return {};

    const info = {};
    const lines = userMessage.split("\n");

    lines.forEach((line) => {
      if (line.includes("Địa chỉ:")) {
        info.address = line.split("Địa chỉ:")[1]?.trim();
      } else if (line.includes("Dự án:")) {
        info.project = line.split("Dự án:")[1]?.trim();
      } else if (line.includes("Diện tích:")) {
        info.area = line.split("Diện tích:")[1]?.trim();
      } else if (line.includes("Loại BĐS:")) {
        info.type = line.split("Loại BĐS:")[1]?.trim();
      }
    });

    return info;
  };

  // Handle analysis click
  const handleAnalysisClick = (analysis) => {
    setCurrentAnalysis(analysis);
    setShowDetailModal(true);
  };

  // Close detail modal
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setCurrentAnalysis(null);
  };

  // Open full report modal
  const openFullReportModal = () => {
    setShowDetailModal(false);
    setShowFullReportModal(true);
  };

  // Close full report modal
  const closeFullReportModal = () => {
    setShowFullReportModal(false);
    setShowDetailModal(true);
  };

  // Hàm lấy tất cả files từ S3 (không trùng lặp)
  const getAllS3Files = (analysis) => {
    if (!analysis?.result?.crawl_response?.data) return [];

    const allFiles = [];
    const seenUrls = new Set();

    // Chỉ lấy files từ downloaded_files (bỏ qua cloudinary_files như yêu cầu)
    const downloadedFiles =
      analysis.result.crawl_response.data.downloaded_files || [];
    downloadedFiles.forEach((file) => {
      if (file.s3_url && !seenUrls.has(file.s3_url)) {
        allFiles.push(file);
        seenUrls.add(file.s3_url);
      }
    });

    return allFiles;
  };

  // Hàm tải file
  const downloadFile = (url, filename) => {
    if (!url) {
      showToast("Không có URL file để tải!", "error");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "file";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast(`Đang tải file: ${filename}`);
    } catch (error) {
      console.error("Lỗi khi tải file:", error);
      showToast("Có lỗi khi tải file!", "error");
    }
  };

  // Function để fetch và hiển thị nội dung markdown
  const viewMarkdownFile = async (url, filename) => {
    if (!url) {
      showToast("Không có URL file để xem!", "error");
      return;
    }

    // Ẩn modal chi tiết trước khi mở modal markdown
    setShowDetailModal(false);

    setLoadingMarkdown(true);
    setMarkdownTitle(filename);
    setShowMarkdownModal(true);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const content = await response.text();
      setMarkdownContent(content);
    } catch (error) {
      console.error("Lỗi khi lấy nội dung markdown:", error);
      showToast("Không thể tải nội dung file markdown!", "error");
      setMarkdownContent("Không thể tải nội dung file.");
    } finally {
      setLoadingMarkdown(false);
    }
  };

  // Function để đóng markdown modal
  const closeMarkdownModal = () => {
    setShowMarkdownModal(false);
    setMarkdownContent("");
    setMarkdownTitle("");
    setLoadingMarkdown(false);

    // Hiển thị lại modal chi tiết khi đóng markdown modal
    if (currentAnalysis) {
      setShowDetailModal(true);
    }
  };

  // Function để kiểm tra file có phải markdown không
  const isMarkdownFile = (filename) => {
    if (!filename) return false;
    const extension = filename.toLowerCase().split(".").pop();
    return extension === "md" || extension === "markdown";
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  // Pagination
  const filteredAnalysis = getFilteredAnalysis();
  const indexOfLastAnalysis = currentPage * analysisPerPage;
  const indexOfFirstAnalysis = indexOfLastAnalysis - analysisPerPage;
  const currentAnalysisData = filteredAnalysis.slice(
    indexOfFirstAnalysis,
    indexOfLastAnalysis
  );
  const totalPages = Math.ceil(filteredAnalysis.length / analysisPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalPages <= 1) return null;

    const renderPageButton = (pageNumber) => (
      <button
        key={pageNumber}
        onClick={() => handlePageChange(pageNumber)}
        className={`px-3 py-1 rounded-lg border text-sm font-medium transition-colors ${
          currentPage === pageNumber
            ? "bg-blue-600 text-white border-blue-600"
            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
        }`}
      >
        {pageNumber}
      </button>
    );

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          Hiển thị {indexOfFirstAnalysis + 1}-
          {Math.min(indexOfLastAnalysis, filteredAnalysis.length)} trong tổng số{" "}
          {filteredAnalysis.length} phân tích
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center gap-1 px-3 py-1 rounded-lg border text-sm font-medium transition-colors ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
          >
            <FiChevronLeft />
            Trước
          </button>

          {startPage > 1 && (
            <>
              {renderPageButton(1)}
              {startPage > 2 && <span className="px-2 text-gray-400">...</span>}
            </>
          )}

          {pageNumbers.map((number) => renderPageButton(number))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-2 text-gray-400">...</span>
              )}
              {renderPageButton(totalPages)}
            </>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center gap-1 px-3 py-1 rounded-lg border text-sm font-medium transition-colors ${
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
          >
            Sau
            <FiChevronRight />
          </button>
        </div>
      </div>
    );
  };

  const SkeletonLoader = () => (
    <div className="space-y-4">
      {[...Array(4)].map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 animate-pulse"
        >
          <div className="flex flex-col gap-3">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-100 rounded"></div>
              <div className="h-4 bg-gray-100 rounded w-5/6"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[...Array(3)].map((_, idx) => (
                <div key={idx} className="h-4 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const totalFiltered = filteredAnalysis.length;
  const pendingFilteredCount = filteredAnalysis.filter(
    (item) => !item.result || item.result.success === undefined
  ).length;
  const hasActiveFilters = Boolean(searchTerm || statusFilter !== "all");
  const detailStatusInfo = currentAnalysis
    ? getStatusInfo(currentAnalysis)
    : null;

  return (
    <div className="admin-price-analysis-management min-h-screen bg-gray-50 p-2 sm:p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === "success" ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 admin-panel">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 admin-card admin-header-card">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
              Admin • Price History
            </p>
            <h2 className="text-2xl font-bold text-gray-900 mt-1">
              Quản lý phân tích giá BĐS
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Theo dõi, tra cứu và kiểm soát kết quả phân tích giá từ histories
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchQueueMessages}
              disabled={loading}
              className="admin-btn admin-btn-solid"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
              Làm mới dữ liệu
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 admin-stats-grid">
          {[
            {
              label: "Tổng phân tích",
              value: stats.totalAnalysis.toLocaleString(),
              icon: <FiActivity />,
              iconBg: "bg-blue-100 text-blue-600",
            },
            {
              label: "Hoàn thành",
              value: stats.completedAnalysis.toLocaleString(),
              icon: <FiCheckCircle />,
              iconBg: "bg-green-100 text-green-600",
            },
            {
              label: "Đang xử lý",
              value: stats.pendingAnalysis.toLocaleString(),
              icon: <FiAlertCircle />,
              iconBg: "bg-yellow-100 text-yellow-600",
            },
            {
              label: "Lỗi",
              value: stats.errorAnalysis.toLocaleString(),
              icon: <FiXCircle />,
              iconBg: "bg-red-100 text-red-600",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 admin-card admin-stat-card"
            >
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl ${card.iconBg} admin-stat-icon`}
              >
                {card.icon}
              </div>
              <div>
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </div>
          ))}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 admin-card admin-stat-card">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl bg-indigo-100 text-indigo-600 admin-stat-icon">
              <FiPercent />
            </div>
            <div>
              <p className="text-sm text-gray-500">Tỷ lệ thành công</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.successRate}%
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 admin-card admin-filters-card">
          <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tìm kiếm
              </label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Theo request ID, tiêu đề hoặc địa chỉ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-200 rounded-2xl py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 admin-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trạng thái
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-200 rounded-2xl py-2 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 admin-select"
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="completed">Hoàn thành</option>
                <option value="pending">Đang xử lý</option>
                <option value="error">Lỗi</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center text-gray-700 font-semibold">
                  <FiActivity className="mr-1" />
                  Đang hiển thị: {totalFiltered}
                </div>
                <div className="flex items-center text-xs text-gray-500">
                  <FiAlertCircle className="mr-1" />
                  Chờ xử lý: {pendingFilteredCount}
                </div>
              </div>
            </div>
            <div className="flex items-end justify-end">
              {hasActiveFilters ? (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-50 admin-btn admin-btn-outline"
                >
                  Xóa bộ lọc
                </button>
              ) : (
                <p className="text-xs text-gray-400">
                  Không có bộ lọc đang áp dụng
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Analysis List */}
        <div className="space-y-4 admin-request-list">
          {loading ? (
            <SkeletonLoader />
          ) : currentAnalysisData.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-6 sm:p-8 text-center shadow-sm">
              <FiFileText className="text-4xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Không có dữ liệu phân tích
              </h3>
              <p className="text-sm text-gray-500">
                {hasActiveFilters
                  ? "Không tìm thấy bản ghi phù hợp với bộ lọc hiện tại."
                  : "Hệ thống chưa ghi nhận phân tích nào trong lịch sử."}
              </p>
            </div>
          ) : (
            currentAnalysisData.map((analysis) => {
              const statusInfo = getStatusInfo(analysis);
              const propertyInfo = extractPropertyInfo(
                analysis.result?.chat_response?.user_message
              );
              const allFiles = getAllS3Files(analysis);
              const analysisTitle =
                analysis.result?.updated_page_data?.current_task_title ||
                analysis.result?.updated_page_data?.page_title ||
                "Phân tích giá BĐS";
              const location =
                propertyInfo.project ||
                propertyInfo.address ||
                analysis.result?.crawl_response?.data?.address ||
                "";
              const previewText =
                analysis.result?.chat_response?.assistant_message ||
                (analysis.result?.error
                  ? typeof analysis.result.error === "string"
                    ? analysis.result.error
                    : JSON.stringify(analysis.result.error)
                  : "Chưa có kết quả phân tích");
              const taskId = analysis.taskId || analysis.result?.task_id;
              const shortTaskId = taskId ? taskId.slice(-8) : "N/A";

              return (
                <div
                  key={analysis.id}
                  className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow border-l-4 ${statusInfo.borderColor} admin-card admin-request-card`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1 space-y-4">
                      <div className="flex flex-wrap gap-3 items-center text-xs sm:text-sm">
                        <div className="flex items-center gap-2">
                          <span className={statusInfo.color}>
                            {statusInfo.icon}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}
                          >
                            {statusInfo.text}
                          </span>
                        </div>
                        <div
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            getPriceType(analysis).bgColor
                          } ${getPriceType(analysis).textColor} border ${
                            getPriceType(analysis).borderColor
                          }`}
                        >
                          {getPriceType(analysis).label}
                        </div>
                        <div className="flex items-center text-gray-500">
                          <FiClock className="mr-1" />
                          {formatDate(
                            analysis.completed_at || analysis.created_at
                          )}
                        </div>
                        <div className="flex items-center text-gray-500">
                          <FiUsers className="mr-1" />
                          {analysis.userId || "Không rõ"}
                        </div>
                        <div className="flex items-center text-gray-500">
                          <FiFileText className="mr-1" />
                          {allFiles.length} file kết quả
                        </div>
                      </div>

                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <FiHome className="text-gray-500" />
                          {analysisTitle}
                        </h3>
                        {location && (
                          <p className="text-sm text-gray-600 flex items-center admin-line-clamp-2 mt-1">
                            <FiMapPin className="mr-1 text-gray-400" />
                            {location}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 text-xs sm:text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full font-mono">
                          <FiArchive className="text-gray-400" />
                          Task: {shortTaskId}
                        </span>
                        <span className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full font-mono">
                          Req: {analysis.request_id?.slice(-8) || "N/A"}
                        </span>
                        {propertyInfo.area && (
                          <span className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                            DT: {propertyInfo.area}
                          </span>
                        )}
                        {propertyInfo.type && (
                          <span className="inline-flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                            {propertyInfo.type}
                          </span>
                        )}
                      </div>

                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-sm text-gray-700 admin-line-clamp-3">
                        {previewText}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 w-full sm:w-48 admin-request-actions">
                      <button
                        onClick={() => handleAnalysisClick(analysis)}
                        className="admin-btn admin-btn-primary"
                      >
                        <FiEye />
                        Chi tiết
                      </button>
                      <button
                        onClick={() => {
                          handleAnalysisClick(analysis);
                          setTimeout(() => openFullReportModal(), 0);
                        }}
                        className="admin-btn admin-btn-primary"
                        style={{ backgroundColor: "#059669" }}
                      >
                        <FiFileText />
                        Báo cáo đầy đủ
                      </button>
                      <button
                        onClick={() => deleteAnalysisRecord(analysis.id)}
                        className="admin-btn admin-btn-danger"
                      >
                        <FiTrash2 />
                        Xóa
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {renderPagination()}
      </div>
      {/* Analysis Detail Modal */}
      {showDetailModal && currentAnalysis && detailStatusInfo && (
        <AnalysisDetailModal
          analysis={currentAnalysis}
          statusInfo={detailStatusInfo}
          formatDate={formatDate}
          onClose={closeDetailModal}
          onDelete={deleteAnalysisRecord}
          getAllS3Files={getAllS3Files}
          isMarkdownFile={isMarkdownFile}
          viewMarkdownFile={viewMarkdownFile}
          downloadFile={downloadFile}
        />
      )}

      {/* Markdown Viewer Modal */}
      {showMarkdownModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                  Markdown Preview
                </p>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900">
                  {markdownTitle || "Nội dung markdown"}
                </h3>
              </div>
              <button
                onClick={closeMarkdownModal}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 admin-scrollbar-thin bg-white">
              {loadingMarkdown ? (
                <div className="flex flex-col items-center justify-center text-gray-500 py-12">
                  <FiRefreshCw className="animate-spin text-4xl text-blue-500 mb-3" />
                  Đang tải nội dung markdown...
                </div>
              ) : (
                <div className="prose max-w-none">
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ node, ...props }) => (
                        <h1
                          className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-3"
                          {...props}
                        />
                      ),
                      h2: ({ node, ...props }) => (
                        <h2
                          className="text-2xl font-semibold text-gray-900 mb-4 mt-8 border-b border-gray-100 pb-2"
                          {...props}
                        />
                      ),
                      h3: ({ node, ...props }) => (
                        <h3
                          className="text-xl font-semibold text-gray-900 mb-3 mt-6"
                          {...props}
                        />
                      ),
                      p: ({ node, ...props }) => (
                        <p
                          className="text-gray-700 mb-4 leading-relaxed"
                          {...props}
                        />
                      ),
                      ul: ({ node, ...props }) => (
                        <ul
                          className="list-disc pl-6 mb-4 text-gray-700"
                          {...props}
                        />
                      ),
                      ol: ({ node, ...props }) => (
                        <ol
                          className="list-decimal pl-6 mb-4 text-gray-700"
                          {...props}
                        />
                      ),
                      code: ({ node, inline, className, children, ...props }) =>
                        inline ? (
                          <code
                            className="bg-gray-100 text-rose-600 px-1.5 py-0.5 rounded"
                            {...props}
                          />
                        ) : (
                          <code
                            className="block bg-gray-900 text-emerald-400 p-4 rounded-xl overflow-auto text-sm"
                            {...props}
                          />
                        ),
                      pre: ({ node, ...props }) => (
                        <pre
                          className="bg-gray-900 text-emerald-400 p-4 rounded-xl overflow-auto mb-4"
                          {...props}
                        />
                      ),
                      table: ({ node, ...props }) => (
                        <table
                          className="w-full border border-gray-200 mb-4"
                          {...props}
                        />
                      ),
                      th: ({ node, ...props }) => (
                        <th
                          className="border border-gray-200 bg-gray-50 px-3 py-2 text-left"
                          {...props}
                        />
                      ),
                      td: ({ node, ...props }) => (
                        <td
                          className="border border-gray-200 px-3 py-2"
                          {...props}
                        />
                      ),
                      a: ({ node, ...props }) => (
                        <a
                          className="text-blue-600 underline"
                          target="_blank"
                          rel="noreferrer"
                          {...props}
                        >
                          {props.children}
                        </a>
                      ),
                    }}
                  >
                    {markdownContent}
                  </Markdown>
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
              <button
                className="px-4 py-2 rounded-2xl bg-gray-900 text-white font-semibold hover:bg-gray-800"
                onClick={closeMarkdownModal}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Report Modal */}
      {showFullReportModal &&
        currentAnalysis &&
        (() => {
          const analysisTitle =
            currentAnalysis.result?.updated_page_data?.current_task_title ||
            currentAnalysis.result?.updated_page_data?.page_title ||
            "Báo cáo phân tích đầy đủ";
          const statusInfo = getStatusInfo(currentAnalysis);

          return (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
              <div className="bg-white rounded-2xl max-w-7xl w-full h-[95vh] flex flex-col overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-600 to-green-600 text-white">
                  <div>
                    <h2 className="text-lg sm:text-2xl font-bold">
                      Báo cáo phân tích đầy đủ
                    </h2>
                    <p className="text-xs sm:text-sm opacity-90 mt-1">
                      {statusInfo.text} •{" "}
                      {formatDate(currentAnalysis.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={closeFullReportModal}
                    className="text-white hover:text-gray-200 text-xl sm:text-2xl p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
                  >
                    ×
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 sm:p-8 admin-scrollbar-thin">
                  <div className="max-w-5xl mx-auto">
                    {/* Task Info */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">
                            Task ID:
                          </span>
                          <span className="ml-2 text-gray-800 font-mono">
                            {currentAnalysis.taskId ||
                              currentAnalysis.result?.task_id ||
                              "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">
                            Trạng thái:
                          </span>
                          <span
                            className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}
                          >
                            {statusInfo.text}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">
                            Thời gian tạo:
                          </span>
                          <span className="ml-2 text-gray-800">
                            {formatDate(currentAnalysis.created_at)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">
                            Cập nhật lần cuối:
                          </span>
                          <span className="ml-2 text-gray-800">
                            {formatDate(
                              currentAnalysis.updated_at ||
                                currentAnalysis.completed_at
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Report Content */}
                    <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
                      <div className="prose prose-lg max-w-none">
                        <Markdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ node, ...props }) => (
                              <h1
                                className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 border-b border-gray-300 pb-3"
                                {...props}
                              />
                            ),
                            h2: ({ node, ...props }) => (
                              <h2
                                className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 mt-8 border-b border-gray-200 pb-2"
                                {...props}
                              />
                            ),
                            h3: ({ node, ...props }) => (
                              <h3
                                className="text-lg sm:text-xl font-bold text-gray-800 mb-3 mt-6"
                                {...props}
                              />
                            ),
                            h4: ({ node, ...props }) => (
                              <h4
                                className="text-base sm:text-lg font-semibold text-gray-800 mb-2 mt-5"
                                {...props}
                              />
                            ),
                            p: ({ node, ...props }) => (
                              <p
                                className="text-gray-700 mb-4 leading-relaxed text-base"
                                {...props}
                              />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul
                                className="list-disc list-inside mb-6 space-y-2 ml-4"
                                {...props}
                              />
                            ),
                            ol: ({ node, ...props }) => (
                              <ol
                                className="list-decimal list-inside mb-6 space-y-2 ml-4"
                                {...props}
                              />
                            ),
                            li: ({ node, ...props }) => (
                              <li
                                className="text-gray-700 text-base"
                                {...props}
                              />
                            ),
                            blockquote: ({ node, ...props }) => (
                              <blockquote
                                className="border-l-4 border-blue-500 pl-6 py-3 bg-blue-50 text-gray-700 italic mb-6 text-base"
                                {...props}
                              />
                            ),
                            code: ({ node, inline, ...props }) =>
                              inline ? (
                                <code
                                  className="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono"
                                  {...props}
                                />
                              ) : (
                                <code
                                  className="block bg-gray-900 text-green-400 p-6 rounded-lg overflow-x-auto text-sm font-mono mb-6"
                                  {...props}
                                />
                              ),
                            pre: ({ node, ...props }) => (
                              <pre
                                className="bg-gray-900 text-green-400 p-6 rounded-lg overflow-x-auto mb-6 text-sm font-mono"
                                {...props}
                              />
                            ),
                            table: ({ node, ...props }) => (
                              <table
                                className="w-full border-collapse border border-gray-300 mb-6 text-base"
                                {...props}
                              />
                            ),
                            th: ({ node, ...props }) => (
                              <th
                                className="border border-gray-300 bg-gray-100 px-4 py-3 text-left font-semibold"
                                {...props}
                              />
                            ),
                            td: ({ node, ...props }) => (
                              <td
                                className="border border-gray-300 px-4 py-3"
                                {...props}
                              />
                            ),
                            a: ({ node, ...props }) => (
                              <a
                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                                target="_blank"
                                rel="noopener noreferrer"
                                {...props}
                              />
                            ),
                            strong: ({ node, ...props }) => (
                              <strong
                                className="font-bold text-gray-900"
                                {...props}
                              />
                            ),
                            em: ({ node, ...props }) => (
                              <em className="italic text-gray-800" {...props} />
                            ),
                          }}
                        >
                          {currentAnalysis.manus_response?.markdown ||
                            currentAnalysis.result?.chat_response
                              ?.assistant_message ||
                            "Không có dữ liệu báo cáo"}
                        </Markdown>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={closeFullReportModal}
                    className="px-4 py-2 rounded-2xl bg-gray-900 text-white font-semibold hover:bg-gray-800"
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default AdminPriceAnalysisManagement;
